import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Combobox } from '@/components/ui/combobox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Pencil, Trash2, CreditCard, LayoutGrid, List } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const paymentTypeColors = {
  ACOMPTE: 'bg-blue-100 text-blue-800',
  SOLDE: 'bg-green-100 text-green-800',
  AUTRE: 'bg-gray-100 text-gray-800',
};

export default function Payments() {
  const { getAuthHeaders } = useAuth();
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [open, setOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [filters, setFilters] = useState({
    clientId: 'all',
    type: 'all',
    moyen: 'all',
    dateFrom: '',
    dateTo: '',
  });
  const [formData, setFormData] = useState({
    appointment_id: '',
    montant: '',
    type: 'SOLDE',
    moyen: 'CARTE',
    paid_at: '',
    notes: '',
  });

  const fetchData = async () => {
    try {
      const [paymentsRes, aptsRes, clientsRes, servicesRes] = await Promise.all([
        axios.get(`${API_URL}/api/payments`, getAuthHeaders()),
        axios.get(`${API_URL}/api/appointments`, getAuthHeaders()),
        axios.get(`${API_URL}/api/clients`, getAuthHeaders()),
        axios.get(`${API_URL}/api/services`, getAuthHeaders()),
      ]);
      const sorted = paymentsRes.data.sort((a, b) => new Date(b.paid_at) - new Date(a.paid_at));
      setPayments(sorted);
      setFilteredPayments(sorted);
      setAppointments(aptsRes.data);
      setClients(clientsRes.data);
      setServices(servicesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = [...payments];

    // Filtrer par client (via appointment_id)
    if (filters.clientId && filters.clientId !== 'all') {
      const clientAppointmentIds = appointments
        .filter(apt => apt.client_id === filters.clientId)
        .map(apt => apt.id);
      filtered = filtered.filter(p => p.appointment_id && clientAppointmentIds.includes(p.appointment_id));
    }

    if (filters.type && filters.type !== 'all') {
      filtered = filtered.filter(p => p.type === filters.type);
    }

    if (filters.moyen && filters.moyen !== 'all') {
      filtered = filtered.filter(p => p.moyen === filters.moyen);
    }

    if (filters.dateFrom) {
      filtered = filtered.filter(p => new Date(p.paid_at) >= new Date(filters.dateFrom));
    }

    if (filters.dateTo) {
      filtered = filtered.filter(p => new Date(p.paid_at) <= new Date(filters.dateTo));
    }

    setFilteredPayments(filtered);
  }, [filters, payments, appointments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        appointment_id: formData.appointment_id === 'none' ? null : formData.appointment_id,
        montant: parseFloat(formData.montant),
      };
      if (editingPayment) {
        await axios.put(`${API_URL}/api/payments/${editingPayment.id}`, payload, getAuthHeaders());
        toast.success('Paiement modifié');
      } else {
        await axios.post(`${API_URL}/api/payments`, payload, getAuthHeaders());
        toast.success('Paiement enregistré');
      }
      fetchData();
      handleClose();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/payments/${deleteId}`, getAuthHeaders());
      toast.success('Paiement supprimé');
      fetchData();
      setDeleteId(null);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setFormData({
      appointment_id: payment.appointment_id || 'none',
      montant: payment.montant.toString(),
      type: payment.type,
      moyen: payment.moyen,
      paid_at: payment.paid_at.slice(0, 16),
      notes: payment.notes || '',
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingPayment(null);
    setFormData({ appointment_id: 'none', montant: '', type: 'SOLDE', moyen: 'CARTE', paid_at: '', notes: '' });
  };

  const getAppointmentInfo = (appointmentId) => {
    if (!appointmentId) return { clientName: 'Paiement libre', dateInfo: '' };
    const apt = appointments.find(a => a.id === appointmentId);
    if (!apt) return { clientName: 'RDV inconnu', dateInfo: '' };
    
    const client = clients.find(c => c.id === apt.client_id);
    const clientName = client ? `${client.prenom} ${client.nom}` : 'Client·e inconnu·e';
    const dateInfo = format(new Date(apt.date), 'dd/MM/yyyy');
    
    return { clientName, dateInfo };
  };

  const getClientNameFromPayment = (payment) => {
    const info = getAppointmentInfo(payment.appointment_id);
    return info.clientName;
  };

  const getServiceName = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    return service ? service.nom : '';
  };
  void getServiceName;

  // Options du sélecteur de RDV : nom client·e + prestation + date + montant, du plus récent au plus ancien
  const appointmentOptions = React.useMemo(() => {
    const paidAptIds = new Set(payments.filter(p => p.appointment_id).map(p => p.appointment_id));
    const sorted = [...appointments].sort((a, b) => new Date(b.date) - new Date(a.date));
    const opts = [{ value: 'none', label: 'Paiement libre' }];
    sorted.forEach(apt => {
      const client = clients.find(c => c.id === apt.client_id);
      const clientName = client ? `${client.prenom} ${client.nom}` : 'Client·e inconnu·e';
      const dateStr = format(new Date(apt.date), 'dd/MM/yyyy HH:mm');
      const text = `${clientName} · ${dateStr} · ${apt.prix}€`;
      const isPaid = paidAptIds.has(apt.id);
      opts.push({
        value: apt.id,
        // searchValue unique (id ajouté) : cmdk exige des valeurs uniques pour que le clic sélectionne
        searchValue: `${text}${isPaid ? ' payé' : ''} ${apt.id}`,
        // display : rendu visuel avec badge éventuel
        display: (
          <span className="flex items-center gap-2 min-w-0">
            {isPaid && (
              <Badge variant="secondary" className="bg-green-100 text-green-800 shrink-0">Déjà payé</Badge>
            )}
            <span className="truncate">{text}</span>
          </span>
        ),
      });
    });
    return opts;
  }, [appointments, clients, services, payments]);

  const handleAppointmentSelect = (value) => {
    const selected = value || 'none';
    if (selected === 'none') {
      setFormData({ ...formData, appointment_id: 'none' });
      return;
    }
    const apt = appointments.find(a => a.id === selected);
    setFormData({
      ...formData,
      appointment_id: selected,
      // Pré-remplit montant et date d'encaissement (modifiables)
      montant: apt ? apt.prix.toString() : formData.montant,
      paid_at: apt ? apt.date.slice(0, 16) : formData.paid_at,
    });
  };

  return (
    <div className="space-y-8" data-testid="payments-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Paiements</h1>
          <p className="text-muted-foreground mt-2">Suivez tous vos encaissements</p>
        </div>
        <div className="flex items-center gap-4">
          <ToggleGroup type="single" value={viewMode} onValueChange={(v) => v && setViewMode(v)} data-testid="view-toggle">
            <ToggleGroupItem value="grid" aria-label="Vue grille">
              <LayoutGrid className="w-4 h-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="list" aria-label="Vue liste">
              <List className="w-4 h-4" />
            </ToggleGroupItem>
          </ToggleGroup>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="btn-add-payment">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau paiement
              </Button>
            </DialogTrigger>
          <DialogContent data-testid="payment-dialog">
            <DialogHeader>
              <DialogTitle>{editingPayment ? 'Modifier' : 'Nouveau'} paiement</DialogTitle>
              <DialogDescription>
                Enregistrez un paiement
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2" data-testid="select-appointment">
                <Label htmlFor="appointment_id">Rendez-vous (optionnel)</Label>
                <Combobox
                  options={appointmentOptions}
                  value={formData.appointment_id || 'none'}
                  onValueChange={handleAppointmentSelect}
                  placeholder="Paiement libre ou lié à un RDV"
                  searchPlaceholder="Rechercher par nom, prestation, date..."
                  emptyText="Aucun rendez-vous trouvé"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="montant">Montant (€) *</Label>
                <Input
                  id="montant"
                  type="number"
                  step="0.01"
                  value={formData.montant}
                  onChange={(e) => setFormData({ ...formData, montant: e.target.value })}
                  required
                  data-testid="input-montant"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="type">Type *</Label>
                  <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                    <SelectTrigger data-testid="select-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ACOMPTE">Acompte</SelectItem>
                      <SelectItem value="SOLDE">Solde</SelectItem>
                      <SelectItem value="AUTRE">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="moyen">Moyen *</Label>
                  <Select value={formData.moyen} onValueChange={(value) => setFormData({ ...formData, moyen: value })}>
                    <SelectTrigger data-testid="select-moyen">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ESPECES">Espèces</SelectItem>
                      <SelectItem value="CARTE">Carte</SelectItem>
                      <SelectItem value="VIREMENT">Virement</SelectItem>
                      <SelectItem value="AUTRE">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="paid_at">Date d&apos;encaissement *</Label>
                <Input
                  id="paid_at"
                  type="datetime-local"
                  value={formData.paid_at}
                  onChange={(e) => setFormData({ ...formData, paid_at: e.target.value })}
                  required
                  data-testid="input-paid-at"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  data-testid="input-notes"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleClose}>Annuler</Button>
                <Button type="submit" data-testid="btn-submit-payment">Enregistrer</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card className="rounded-xl border-border/60" data-testid="filters-card">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label htmlFor="filter-client">Client·e</Label>
              <Combobox
                options={[
                  { value: 'all', label: 'Tou·te·s les client·e·s' },
                  ...clients.map(c => ({ value: c.id, label: `${c.prenom} ${c.nom}` }))
                ]}
                value={filters.clientId}
                onValueChange={(value) => setFilters({ ...filters, clientId: value })}
                placeholder="Tou·te·s les client·e·s"
                searchPlaceholder="Rechercher..."
                emptyText="Aucun·e client·e trouvé·e"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="filter-type">Type</Label>
              <Select value={filters.type} onValueChange={(value) => setFilters({ ...filters, type: value })}>
                <SelectTrigger data-testid="filter-type">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  <SelectItem value="ACOMPTE">Acompte</SelectItem>
                  <SelectItem value="SOLDE">Solde</SelectItem>
                  <SelectItem value="AUTRE">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="filter-moyen">Moyen</Label>
              <Select value={filters.moyen} onValueChange={(value) => setFilters({ ...filters, moyen: value })}>
                <SelectTrigger data-testid="filter-moyen">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les moyens</SelectItem>
                  <SelectItem value="ESPECES">Espèces</SelectItem>
                  <SelectItem value="CARTE">Carte</SelectItem>
                  <SelectItem value="VIREMENT">Virement</SelectItem>
                  <SelectItem value="AUTRE">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="filter-date-from">Date début</Label>
              <Input
                id="filter-date-from"
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                data-testid="filter-date-from"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="filter-date-to">Date fin</Label>
              <Input
                id="filter-date-to"
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                data-testid="filter-date-to"
              />
            </div>
          </div>
          {(filters.type !== 'all' || filters.moyen !== 'all' || filters.dateFrom || filters.dateTo) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFilters({ type: 'all', moyen: 'all', dateFrom: '', dateTo: '' })}
              className="mt-4"
              data-testid="btn-clear-filters"
            >
              Réinitialiser les filtres
            </Button>
          )}
        </CardContent>
      </Card>

      {filteredPayments.length === 0 && payments.length > 0 ? (
        <Card className="rounded-xl border-border/60">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CreditCard className="w-16 h-16 text-muted-foreground mb-4" strokeWidth={1} />
            <p className="text-muted-foreground">Aucun paiement ne correspond aux filtres</p>
          </CardContent>
        </Card>
      ) : filteredPayments.length === 0 ? (
        <Card className="rounded-xl border-border/60">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CreditCard className="w-16 h-16 text-muted-foreground mb-4" strokeWidth={1} />
            <p className="text-muted-foreground">Aucun paiement pour le moment</p>
          </CardContent>
        </Card>
      ) : viewMode === 'list' ? (
        <Card className="rounded-xl border-border/60">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client·e</TableHead>
                <TableHead>Date RDV</TableHead>
                <TableHead>Encaissé le</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Moyen</TableHead>
                <TableHead>Montant</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPayments.map((payment) => {
                const aptInfo = getAppointmentInfo(payment.appointment_id);
                return (
                  <TableRow key={payment.id} data-testid={`payment-row-${payment.id}`}>
                    <TableCell className="font-medium">{aptInfo.clientName}</TableCell>
                    <TableCell>{aptInfo.dateInfo || '-'}</TableCell>
                    <TableCell>{format(new Date(payment.paid_at), 'dd/MM/yyyy HH:mm')}</TableCell>
                    <TableCell><Badge className={paymentTypeColors[payment.type]}>{payment.type}</Badge></TableCell>
                    <TableCell><Badge variant="outline">{payment.moyen}</Badge></TableCell>
                    <TableCell className="font-semibold text-primary">{payment.montant.toFixed(2)} €</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(payment)} data-testid={`btn-edit-${payment.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteId(payment.id)} className="text-destructive hover:text-destructive" data-testid={`btn-delete-${payment.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredPayments.map((payment) => {
            const aptInfo = getAppointmentInfo(payment.appointment_id);
            return (
              <Card
                key={payment.id}
                className="rounded-xl border-border/60 hover:border-primary/20 transition-colors"
                data-testid={`payment-card-${payment.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold">{aptInfo.clientName}</h3>
                        <Badge className={paymentTypeColors[payment.type]}>{payment.type}</Badge>
                        <Badge variant="outline">{payment.moyen}</Badge>
                      </div>
                      {aptInfo.dateInfo && (
                        <p className="text-sm text-muted-foreground">RDV du {aptInfo.dateInfo}</p>
                      )}
                      <p className="text-sm text-muted-foreground">
                        Encaissé le {format(new Date(payment.paid_at), 'PPP à HH:mm', { locale: fr })}
                      </p>
                      {payment.notes && (
                        <p className="text-sm text-muted-foreground mt-2 italic">{payment.notes}</p>
                      )}
                    </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary">{payment.montant.toFixed(2)} €</p>
                    <div className="flex gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(payment)}
                        data-testid={`btn-edit-${payment.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteId(payment.id)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`btn-delete-${payment.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Êtes-vous sûr ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le paiement sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btn-cancel-delete">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="btn-confirm-delete">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}